import {
  EXCLUSIVE_DUTCH_ORDER_REACTOR_ABI,
  FEE_MODULE_ABI,
  IDENTITY_ADAPTER_ABI,
  IDENTITY_REGISTRY_ABI,
  MOCK_ERC20_ABI,
  ORDER_QUOTER_ABI,
  PERMIT2_ABI,
  POOL_MANAGER_ABI,
  POOL_MODIFY_LIQUIDITY_TEST_ABI,
  POOL_SWAP_TEST_ABI,
  POSITION_MANAGER_ABI,
  PRIORITY_ORDER_REACTOR_ABI,
  REPUTATION_REGISTRY_ABI,
  STATE_VIEW_ABI,
  UNIVERSAL_ROUTER_ABI,
  V2_DUTCH_ORDER_REACTOR_ABI,
  V2_FACTORY_ABI,
  V2_PAIR_ABI,
  V2_ROUTER_ABI,
  V3_FACTORY_ABI,
  V3_NFT_POSITION_MANAGER_ABI,
  V3_POOL_ABI,
  V3_QUOTER_V2_ABI,
  V3_SWAP_ROUTER_ABI,
  V4_QUOTER_ABI,
  VAULT_CORE_ABI,
  VAULT_FACTORY_ABI,
  WETH9_ABI,
} from "../../lib/abis";

import type { UniswapDeployment } from "../../types";
import type { Address } from "viem";

// ---------------------------------------------------------------------------
// ABI function type
// ---------------------------------------------------------------------------

export interface AbiFunction {
  name: string;
  type: "function";
  inputs: {
    name: string;
    type: string;
    components?: { name: string; type: string }[];
  }[];
  outputs: { name: string; type: string }[];
  stateMutability: string;
}

// ---------------------------------------------------------------------------
// Contract groups & types
// ---------------------------------------------------------------------------

export type ContractGroup =
  | "Pinned"
  | "Tokens"
  | "Foundation"
  | "V2"
  | "V3"
  | "V4"
  | "Universal Router"
  | "UniswapX"
  | "ERC-8004"
  | "Vault"
  | "Custom";

export type ContractType =
  | "ERC-20"
  | "Factory"
  | "Router"
  | "Pool"
  | "Position Manager"
  | "Quoter"
  | "Reactor"
  | "Registry"
  | "Hook"
  | "WETH"
  | "Unknown";

export interface ContractOption {
  label: string;
  value: string;
  address: Address;
  abiKey: string;
  group: ContractGroup;
  contractType: ContractType;
  tokenSymbol?: string;
}

export interface FunctionResult {
  value: string;
  raw: unknown;
  timestamp: number;
}

// ---------------------------------------------------------------------------
// ABI registry — maps abiKey to ABI
// ---------------------------------------------------------------------------

export const KNOWN_ABIS: Record<string, readonly unknown[]> = {
  MockERC20: MOCK_ERC20_ABI,
  V2Pair: V2_PAIR_ABI,
  V2Factory: V2_FACTORY_ABI,
  V2Router: V2_ROUTER_ABI,
  V3Factory: V3_FACTORY_ABI,
  V3Pool: V3_POOL_ABI,
  V3SwapRouter: V3_SWAP_ROUTER_ABI,
  V3PositionManager: V3_NFT_POSITION_MANAGER_ABI,
  V3QuoterV2: V3_QUOTER_V2_ABI,
  V4Quoter: V4_QUOTER_ABI,
  WETH9: WETH9_ABI,
  StateView: STATE_VIEW_ABI,
  PoolSwapTest: POOL_SWAP_TEST_ABI,
  PoolModifyLiquidityTest: POOL_MODIFY_LIQUIDITY_TEST_ABI,
  IdentityRegistry: IDENTITY_REGISTRY_ABI,
  Permit2: PERMIT2_ABI,
  ReputationRegistry: REPUTATION_REGISTRY_ABI,
  PoolManager: POOL_MANAGER_ABI,
  PositionManager: POSITION_MANAGER_ABI,
  UniversalRouter: UNIVERSAL_ROUTER_ABI,
  ExclusiveDutchOrderReactor: EXCLUSIVE_DUTCH_ORDER_REACTOR_ABI,
  PriorityOrderReactor: PRIORITY_ORDER_REACTOR_ABI,
  V2DutchOrderReactor: V2_DUTCH_ORDER_REACTOR_ABI,
  OrderQuoter: ORDER_QUOTER_ABI,
  VaultFactory: VAULT_FACTORY_ABI,
  VaultCore: VAULT_CORE_ABI,
  FeeModule: FEE_MODULE_ABI,
  IdentityAdapter: IDENTITY_ADAPTER_ABI,
};

// ---------------------------------------------------------------------------
// Build contract options from deployment
// ---------------------------------------------------------------------------

export function buildContractOptions(
  deployment: UniswapDeployment,
): ContractOption[] {
  const opts: ContractOption[] = [];

  // Tokens
  if (deployment.tokens) {
    const tokenEntries: [string, string, Address][] = [
      ["WETH", "weth", deployment.tokens.weth],
      ["USDC", "usdc", deployment.tokens.usdc],
      ["USDT", "usdt", deployment.tokens.usdt],
      ["DAI", "dai", deployment.tokens.dai],
      ["WBTC", "wbtc", deployment.tokens.wbtc],
    ];
    for (const [label, sym, addr] of tokenEntries) {
      opts.push({
        label,
        value: `token-${sym}`,
        address: addr,
        abiKey: sym === "weth" ? "WETH9" : "MockERC20",
        group: "Tokens",
        contractType: sym === "weth" ? "WETH" : "ERC-20",
        tokenSymbol: label,
      });
    }
  }

  // Foundation
  opts.push({
    label: "WETH9",
    value: "foundation-weth9",
    address: deployment.foundation.weth9,
    abiKey: "WETH9",
    group: "Foundation",
    contractType: "WETH",
  });
  opts.push({
    label: "Permit2",
    value: "foundation-permit2",
    address: deployment.foundation.permit2,
    abiKey: "Permit2",
    group: "Foundation",
    contractType: "Unknown",
  });

  // V2
  opts.push({
    label: "V2 Factory",
    value: "v2-factory",
    address: deployment.v2.factory,
    abiKey: "V2Factory",
    group: "V2",
    contractType: "Factory",
  });
  opts.push({
    label: "V2 Router",
    value: "v2-router02",
    address: deployment.v2.router02,
    abiKey: "V2Router",
    group: "V2",
    contractType: "Router",
  });

  // V3
  opts.push({
    label: "V3 Factory",
    value: "v3-factory",
    address: deployment.v3.factory,
    abiKey: "V3Factory",
    group: "V3",
    contractType: "Factory",
  });
  opts.push({
    label: "V3 Router",
    value: "v3-router",
    address: deployment.v3.router,
    abiKey: "V3SwapRouter",
    group: "V3",
    contractType: "Router",
  });
  opts.push({
    label: "V3 Position Manager",
    value: "v3-nonfungiblePositionManager",
    address: deployment.v3.nonfungiblePositionManager,
    abiKey: "V3PositionManager",
    group: "V3",
    contractType: "Position Manager",
  });
  opts.push({
    label: "V3 QuoterV2",
    value: "v3-quoterV2",
    address: deployment.v3.quoterV2,
    abiKey: "V3QuoterV2",
    group: "V3",
    contractType: "Quoter",
  });

  // V4
  opts.push({
    label: "V4 PoolManager",
    value: "v4-poolManager",
    address: deployment.v4.poolManager,
    abiKey: "PoolManager",
    group: "V4",
    contractType: "Pool",
  });
  opts.push({
    label: "V4 Position Manager",
    value: "v4-positionManager",
    address: deployment.v4.positionManager,
    abiKey: "PositionManager",
    group: "V4",
    contractType: "Position Manager",
  });
  opts.push({
    label: "V4 StateView",
    value: "v4-stateView",
    address: deployment.v4.stateView,
    abiKey: "StateView",
    group: "V4",
    contractType: "Unknown",
  });
  opts.push({
    label: "V4 Quoter",
    value: "v4-quoter",
    address: deployment.v4.quoter,
    abiKey: "V4Quoter",
    group: "V4",
    contractType: "Quoter",
  });
  opts.push({
    label: "V4 SwapTest",
    value: "v4-poolSwapTest",
    address: deployment.v4.poolSwapTest,
    abiKey: "PoolSwapTest",
    group: "V4",
    contractType: "Router",
  });
  opts.push({
    label: "V4 LiquidityTest",
    value: "v4-poolModifyLiquidityTest",
    address: deployment.v4.poolModifyLiquidityTest,
    abiKey: "PoolModifyLiquidityTest",
    group: "V4",
    contractType: "Router",
  });

  // Universal Router
  opts.push({
    label: "Universal Router",
    value: "universalRouter-router",
    address: deployment.universalRouter.router,
    abiKey: "UniversalRouter",
    group: "Universal Router",
    contractType: "Router",
  });

  // UniswapX
  opts.push({
    label: "Dutch Order Reactor",
    value: "uniswapX-exclusiveDutchOrderReactor",
    address: deployment.uniswapX.exclusiveDutchOrderReactor,
    abiKey: "ExclusiveDutchOrderReactor",
    group: "UniswapX",
    contractType: "Reactor",
  });
  opts.push({
    label: "Priority Order Reactor",
    value: "uniswapX-priorityOrderReactor",
    address: deployment.uniswapX.priorityOrderReactor,
    abiKey: "PriorityOrderReactor",
    group: "UniswapX",
    contractType: "Reactor",
  });
  opts.push({
    label: "V2 Dutch Order Reactor",
    value: "uniswapX-v2DutchOrderReactor",
    address: deployment.uniswapX.v2DutchOrderReactor,
    abiKey: "V2DutchOrderReactor",
    group: "UniswapX",
    contractType: "Reactor",
  });
  opts.push({
    label: "Order Quoter",
    value: "uniswapX-orderQuoter",
    address: deployment.uniswapX.orderQuoter,
    abiKey: "OrderQuoter",
    group: "UniswapX",
    contractType: "Quoter",
  });

  // ERC-8004
  opts.push({
    label: "Identity Registry",
    value: "erc8004-identityRegistry",
    address: deployment.erc8004.identityRegistry,
    abiKey: "IdentityRegistry",
    group: "ERC-8004",
    contractType: "Registry",
  });
  opts.push({
    label: "Reputation Registry",
    value: "erc8004-reputationRegistry",
    address: deployment.erc8004.reputationRegistry,
    abiKey: "ReputationRegistry",
    group: "ERC-8004",
    contractType: "Registry",
  });

  // Vault
  if (deployment.vault) {
    opts.push({
      label: "Vault Factory",
      value: "vault-vaultFactory",
      address: deployment.vault.vaultFactory,
      abiKey: "VaultFactory",
      group: "Vault",
      contractType: "Factory",
    });
    opts.push({
      label: "Vault Implementation",
      value: "vault-vaultImplementation",
      address: deployment.vault.vaultImplementation,
      abiKey: "VaultCore",
      group: "Vault",
      contractType: "Unknown",
    });
    opts.push({
      label: "Fee Module",
      value: "vault-feeModule",
      address: deployment.vault.feeModule,
      abiKey: "FeeModule",
      group: "Vault",
      contractType: "Unknown",
    });
    opts.push({
      label: "Identity Adapter",
      value: "vault-identityAdapter",
      address: deployment.vault.identityAdapter,
      abiKey: "IdentityAdapter",
      group: "Vault",
      contractType: "Registry",
    });
  }

  return opts;
}

// ---------------------------------------------------------------------------
// Contract group ordering for display
// ---------------------------------------------------------------------------

export const GROUP_ORDER: ContractGroup[] = [
  "Pinned",
  "Tokens",
  "Foundation",
  "V2",
  "V3",
  "V4",
  "Universal Router",
  "UniswapX",
  "ERC-8004",
  "Vault",
  "Custom",
];

// ---------------------------------------------------------------------------
// Group badge colors
// ---------------------------------------------------------------------------

export const GROUP_COLORS: Record<string, string> = {
  Tokens: "bg-blue-500/20 text-blue-400",
  Foundation: "bg-gray-500/20 text-gray-400",
  V2: "bg-green-500/20 text-green-400",
  V3: "bg-blue-500/20 text-blue-400",
  V4: "bg-purple-500/20 text-purple-400",
  "Universal Router": "bg-pink-500/20 text-pink-400",
  UniswapX: "bg-amber-500/20 text-amber-400",
  "ERC-8004": "bg-cyan-500/20 text-cyan-400",
  Vault: "bg-emerald-500/20 text-emerald-400",
  Custom: "bg-gray-500/20 text-gray-400",
  Pinned: "bg-yellow-500/20 text-yellow-400",
};
